import { useState, useEffect } from 'react';
import { Bell, Shield, AlertTriangle, CheckCircle, Globe, Lock, Mail, Network, RefreshCw, Eye } from 'lucide-react';

const ALERT_ICONS: Record<string, any> = {
  cert_expiry: Lock,
  header_removed: Shield,
  new_subdomain: Network,
  site_down: AlertTriangle,
  dns_changed: Mail,
  default: Bell,
};

const ALERT_COLORS: Record<string, string> = {
  critical: 'bg-red-50 border-red-200 text-red-800',
  high: 'bg-orange-50 border-orange-200 text-orange-800',
  medium: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  low: 'bg-blue-50 border-blue-200 text-blue-800',
  info: 'bg-gray-50 border-gray-200 text-gray-600',
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev'}/api/monitoring/alerts`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('xfreescan_token')}` } }
      );
      const data = await resp.json();
      if (data.success) setAlerts(data.data || []);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  const markRead = async (alertId: string) => {
    try {
      await fetch(
        `${import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev'}/api/monitoring/alerts/${alertId}/read`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('xfreescan_token')}` },
        }
      );
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: 1 } : a));
    } catch {}
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Alertas de Monitoreo</h1>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-red-500 text-white text-xs font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        <button onClick={loadAlerts} className="btn-secondary text-sm">
          <RefreshCw className="h-4 w-4" /> Actualizar
        </button>
      </div>

      <p className="text-sm text-gray-500">
        xFreeScan monitorea sus targets cada 6 horas. Aqui vera cambios detectados: headers removidos, certificados por expirar, nuevos subdominios, y mas.
      </p>

      {alerts.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-700">Sin alertas</p>
          <p className="text-sm text-gray-500 mt-1">Todo esta en orden. Las alertas apareceran cuando se detecte un cambio.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(alert => {
            const IconComp = ALERT_ICONS[alert.alert_type] || ALERT_ICONS.default;
            const colorClass = ALERT_COLORS[alert.severity] || ALERT_COLORS.info;
            return (
              <div key={alert.id} className={`rounded-xl border p-4 ${colorClass} ${!alert.is_read ? 'ring-2 ring-purple-300' : 'opacity-75'}`}>
                <div className="flex items-start gap-3">
                  <IconComp className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm">{alert.title}</h3>
                        {!alert.is_read && <span className="h-2 w-2 rounded-full bg-purple-500" />}
                      </div>
                      <span className="text-[10px] text-gray-500">
                        {new Date(alert.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{alert.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {alert.target}</span>
                      {alert.previous_value && <span>Antes: {alert.previous_value.substring(0, 50)}</span>}
                      {alert.current_value && <span>Ahora: {alert.current_value.substring(0, 50)}</span>}
                    </div>
                    {!alert.is_read && (
                      <button onClick={() => markRead(alert.id)} className="mt-2 text-xs text-purple-700 hover:underline flex items-center gap-1">
                        <Eye className="h-3 w-3" /> Marcar como leida
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
